// The deep-teal rail (signed language). Labels show from 1180px; below that the
// rail is icons with the label in a tooltip, matching the platform's iPad tier.
//
// Labels come from the dictionary by id (board card AIW-CN): the rail is the first
// thing a Chinese-reading member sees, so it is the first thing that had to read in
// Chinese. Product and supplier names are never dictionary copy — nothing on this
// rail is a product name.
import { NavLink } from "react-router-dom";
import { Boxes, Briefcase, Building2, CalendarDays, CalendarRange, Columns3, FolderOpen, Globe2, Hammer, Inbox, LibraryBig, ListChecks, Mail, MessagesSquare, PackageSearch, RotateCcw, Settings, Truck, UserRound, Users, Radar, Wrench } from "lucide-react";
import { cn } from "@aiw/ui";
import { useT } from "../lib/i18n";
import type { MessageId } from "@aiw/i18n";

const GROUPS: { label: MessageId; items: { to: string; label: MessageId; icon: React.ComponentType<{ className?: string }> }[] }[] = [
  { label: "rail.group.work", items: [
    { to: "/", label: "rail.today", icon: CalendarRange },
    { to: "/leads", label: "rail.leads", icon: Inbox },
    { to: "/hipages", label: "rail.hipages", icon: Radar },
    { to: "/pipeline", label: "rail.pipeline", icon: Columns3 },
    { to: "/catalogue", label: "rail.catalogue", icon: LibraryBig },
    { to: "/service-library", label: "rail.serviceLibrary", icon: Wrench },
    { to: "/follow-ups", label: "rail.followUps", icon: RotateCcw },
    { to: "/contacts", label: "rail.contacts", icon: Users },
    { to: "/accounts", label: "rail.accounts", icon: Building2 },
    { to: "/jobs", label: "rail.jobs", icon: Briefcase },
    { to: "/archive", label: "rail.documents", icon: FolderOpen },
    { to: "/calendar", label: "rail.calendar", icon: CalendarDays },
  ] },
  { label: "rail.group.grow", items: [
    { to: "/website", label: "rail.website", icon: Globe2 },
    { to: "/members", label: "rail.members", icon: UserRound },
  ] },
  { label: "rail.group.supply", items: [{ to: "/communications", label: "rail.communications", icon: MessagesSquare }, { to: "/mail", label: "rail.mail", icon: Mail }, { to: "/suppliers", label: "rail.suppliers", icon: Truck }, { to: "/purchase-orders", label: "rail.purchaseOrders", icon: PackageSearch }, { to: "/installers", label: "rail.installers", icon: Hammer }, { to: "/inventory", label: "rail.inventory", icon: Boxes }] },
  { label: "rail.group.system", items: [{ to: "/build-tracker", label: "rail.buildTracker", icon: ListChecks }, { to: "/settings", label: "rail.settings", icon: Settings }] },
];

export function Rail({ badges }: { badges: Record<string, number> }) {
  const { t } = useT();
  return (
    <nav className="flex h-full w-14 shrink-0 flex-col bg-rail text-rail-foreground [@media(min-width:1180px)]:w-52" aria-label={t("rail.sections")}>
      <div className="flex items-center gap-2 px-3 py-4">
        {/* The Artemis Flooring symbol (packages/ui/brand, 6 Oct 2026); the small cut keeps two floor seams at this size. */}
        <img src="/brand/artemis-symbol-small-on-navy.svg" alt="" width={28} height={28} className="h-7 w-7 shrink-0" />
        <span className="hidden leading-tight [@media(min-width:1180px)]:block">
          <span className="block font-display text-sm font-semibold">Artemis</span>
          <span className="block font-mono text-micro text-rail-muted">intelligent web</span>
        </span>
      </div>
      {GROUPS.map((g) => (
        <div key={g.label} className="mt-2">
          <div className="hidden px-4 pb-1 font-mono text-micro uppercase tracking-wider text-rail-muted [@media(min-width:1180px)]:block">{t(g.label)}</div>
          {g.items.map((it) => {
            const n = badges[it.to];
            const label = t(it.label);
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                title={label}
                className={({ isActive }) => cn("mx-2 my-0.5 flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-rail-foreground/90 hover:bg-rail-active", isActive && "bg-rail-active font-medium text-rail-foreground")}
              >
                <it.icon className="h-4 w-4 shrink-0" />
                <span className="hidden flex-1 truncate [@media(min-width:1180px)]:inline">{label}</span>
                {n ? <span className="hidden rounded-full bg-warning px-1.5 font-mono text-micro text-warning-foreground [@media(min-width:1180px)]:inline">{n}</span> : null}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
